import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "../../redux/network/https";
import publicClient from "../publicClient";
import { endPoints, queryKeys } from "../../redux/network/constant";

// ─── Helpers ─────────────────────────────────────────────────────────────────

// ─── My Teams (list) ─────────────────────────────────────────────────────────

export const fetchMyTeams = async ({ queryKey }) => {
  const [, , params = {}] = queryKey;
  const { data } = await axiosInstance.get(endPoints.MyTeams, {
    params: {
      page: params.page,
      page_size: params.pageSize,
      ...(params.search ? { search: params.search } : {}),
      ...(params.type && params.type !== "all" ? { type: params.type } : {}),
    },
  });
  const payload = data?.data ?? data ?? [];
  if (Array.isArray(payload)) {
    return {
      results: payload,
      total_count: payload.length,
      total_pages: 1,
      page: params.page,
      page_size: params.pageSize,
      has_next: false,
      has_previous: params.page > 1,
    };
  }
  return payload;
};

export const useMyTeams = (options = true) => {
  const normalized = typeof options === "boolean" ? { enabled: options } : options || {};
  const params = {
    page: normalized.page || 1,
    pageSize: normalized.pageSize || normalized.page_size || 100,
    search: normalized.search || "",
    type: normalized.type || "all",
  };

  return useQuery({
    queryKey: queryKeys.MyTeams(params),
    queryFn: fetchMyTeams,
    enabled: normalized.enabled !== false,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
};

// ─── Team Detail ─────────────────────────────────────────────────────────────

const fetchTeamDetail = async (teamId) => {
  const { data } = await axiosInstance.get(endPoints.TeamDetail(teamId));
  return data?.data ?? data;
};

export const useTeamDetail = (teamId, enabled = true) =>
  useQuery({
    queryKey: queryKeys.TeamDetail(teamId),
    queryFn: () => fetchTeamDetail(teamId),
    enabled: !!teamId && enabled,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });

// ─── Team Members ────────────────────────────────────────────────────────────

const fetchTeamMembers = async (teamId) => {
  const { data } = await axiosInstance.get(endPoints.TeamMembers(teamId));
  return data?.data ?? data ?? [];
};

export const useTeamMembers = (teamId, enabled = true) =>
  useQuery({
    queryKey: queryKeys.TeamMembers(teamId),
    queryFn: () => fetchTeamMembers(teamId),
    enabled: !!teamId && enabled,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });

// ─── Team Invites ────────────────────────────────────────────────────────────

const fetchTeamInvites = async (teamId) => {
  const { data } = await axiosInstance.get(endPoints.TeamInvites(teamId));
  return data?.data ?? data ?? [];
};

export const useTeamInvites = (teamId, enabled = true) =>
  useQuery({
    queryKey: queryKeys.TeamInvites(teamId),
    queryFn: () => fetchTeamInvites(teamId),
    enabled: !!teamId && enabled,
    staleTime: 15 * 1000,
    refetchOnWindowFocus: false,
  });

// ─── Team Storage ────────────────────────────────────────────────────────────

const fetchTeamStorage = async (teamId) => {
  const { data } = await axiosInstance.get(endPoints.TeamStorage(teamId));
  return data?.data ?? data;
};

export const useTeamStorage = (teamId, enabled = true) =>
  useQuery({
    queryKey: queryKeys.TeamStorage(teamId),
    queryFn: () => fetchTeamStorage(teamId),
    enabled: !!teamId && enabled,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

// ─── Invalidation helper ─────────────────────────────────────────────────────

const invalidateTeamQueries = (queryClient, teamId) => {
  queryClient.invalidateQueries({ queryKey: ["teams", "detail", teamId] });
  queryClient.invalidateQueries({ queryKey: ["teams", "members", teamId] });
  queryClient.invalidateQueries({ queryKey: ["teams", "invites", teamId] });
  queryClient.invalidateQueries({ queryKey: ["teams", "storage", teamId] });
  queryClient.invalidateQueries({ queryKey: ["teams", "my"] });
};

// ─── Mutations ───────────────────────────────────────────────────────────────

export const useCreateTeam = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await axiosInstance.post(endPoints.TeamCreate, payload);
      return data?.data ?? data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teams", "my"] });
    },
  });
};

export const useUpdateTeam = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ teamId, payload }) => {
      const { data } = await axiosInstance.patch(endPoints.TeamUpdate(teamId), payload);
      return data?.data ?? data;
    },
    onSuccess: (_res, vars) => {
      invalidateTeamQueries(queryClient, vars.teamId);
    },
  });
};

export const useInviteTeamMember = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ teamId, payload }) => {
      const { data } = await axiosInstance.post(endPoints.TeamInviteCreate(teamId), payload);
      return data?.data ?? data;
    },
    onSuccess: (_res, vars) => {
      invalidateTeamQueries(queryClient, vars.teamId);
    },
  });
};

export const useRequestSecurityOtp = () =>
  useMutation({
    mutationFn: async (purpose) => {
      const { data } = await axiosInstance.post(endPoints.SecurityOtpRequest, { purpose });
      return data?.data ?? data;
    },
  });

export const useVerifySecurityOtp = () =>
  useMutation({
    mutationFn: async ({ challengeId, code }) => {
      const { data } = await axiosInstance.post(endPoints.SecurityOtpVerify, {
        challenge_id: challengeId,
        code,
      });
      return data?.data ?? data;
    },
  });

export const useDeleteTeamInvite = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ teamId, inviteId }) => {
      const { data } = await axiosInstance.delete(endPoints.TeamInviteDelete(teamId, inviteId));
      return data?.data ?? data;
    },
    onSuccess: (_res, vars) => {
      invalidateTeamQueries(queryClient, vars.teamId);
    },
  });
};

export const useUpdateTeamMemberRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ teamId, userId, payload }) => {
      const { data } = await axiosInstance.post(endPoints.TeamMemberRole(teamId, userId), payload);
      return data?.data ?? data;
    },
    onSuccess: (_res, vars) => {
      invalidateTeamQueries(queryClient, vars.teamId);
    },
  });
};

export const useUpdateTeamMemberAccess = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ teamId, userId, payload }) => {
      const { data } = await axiosInstance.patch(endPoints.TeamMemberAccess(teamId, userId), payload);
      return data?.data ?? data;
    },
    onSuccess: (_res, vars) => {
      invalidateTeamQueries(queryClient, vars.teamId);
    },
  });
};

export const useRemoveTeamMember = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ teamId, userId }) => {
      const { data } = await axiosInstance.delete(endPoints.TeamMemberRemove(teamId, userId));
      return data?.data ?? data;
    },
    onSuccess: (_res, vars) => {
      invalidateTeamQueries(queryClient, vars.teamId);
    },
  });
};

export const useTransferTeamOwnership = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ teamId, payload }) => {
      const { data } = await axiosInstance.post(endPoints.TeamTransferOwnership(teamId), payload);
      return data?.data ?? data;
    },
    onSuccess: (_res, vars) => {
      invalidateTeamQueries(queryClient, vars.teamId);
    },
  });
};

// ─── Team Delete ─────────────────────────────────────────────────────────────

export const useDeleteTeam = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ teamId }) => {
      const { data } = await axiosInstance.delete(endPoints.TeamDelete(teamId));
      return data?.data ?? data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teams", "my"] });
    },
  });
};

// ─── Team Invite Accept / Decline ─────────────────────────────────────────────

const fetchTeamInviteContext = async ({ queryKey }) => {
  const [, , , token] = queryKey;
  const { data } = await publicClient.get(endPoints.TeamInviteDetail(token));
  return data?.data ?? data;
};

export const useGetTeamInviteContext = (token, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.TeamInviteDetail(token),
    queryFn: fetchTeamInviteContext,
    enabled: !!token && enabled,
    retry: false,
    staleTime: 0,
  });
};

export const useAcceptTeamInvite = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ token }) => {
      const { data } = await axiosInstance.post(endPoints.TeamInviteAccept(token));
      return data?.data ?? data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teams", "my"] });
    },
  });
};

export const useDeclineTeamInvite = () => {
  return useMutation({
    mutationFn: async ({ token }) => {
      const { data } = await axiosInstance.post(endPoints.TeamInviteDecline(token));
      return data?.data ?? data;
    },
  });
};
